import { Link, useLocation } from 'react-router-dom';
import { MapPin, Calendar, Users, Clock, UtensilsCrossed, Lock } from 'lucide-react';
import { useI18n } from '../i18n';
import { categoryById, PER_DIEM_LABELS } from '../data/demo';
import { OnlineDot } from './Presence';
import { UserName } from './MessagingContext';
import {
  VerifiedBadge, ProfessionalPublishedBadge, SubstitutionBadge, UrgentBadge, CategoryTag, DemoBadge
} from './Badges';

const formatDate = (iso, locale) => {
  try {
    const map = { es: 'es-ES', ca: 'ca-ES', en: 'en-GB' };
    return new Date(iso).toLocaleDateString(map[locale] || 'es-ES', { day: '2-digit', month: 'short' });
  } catch { return iso; }
};

export const JobCard = ({ job, compact = false, closed = false }) => {
  const { t, locale } = useI18n();
  const location = useLocation();
  const cat = categoryById(job.category);
  const urgent = job.urgent && !closed;
  return (
    <Link
      to={`/ofertas/${job.id}`}
      state={location.pathname === '/ofertas' ? { backgroundLocation: location } : undefined}
      data-testid={`job-card-${job.id}`}
      data-urgent={urgent ? 'true' : 'false'}
      data-closed={closed ? 'true' : 'false'}
      className={`sc-card sc-job-zoom ${urgent ? 'sc-card-urgent' : ''} ${closed ? 'opacity-70' : ''} p-6 flex flex-col justify-between h-full group focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 focus:ring-offset-[#0B0C0E]`}
    >
      <div>
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {cat && <CategoryTag name={cat.name} accent={cat.accent} />}
          {job.substitution && <SubstitutionBadge label={`🔄 ${t('badge.substitution')}`} />}
          {urgent && <UrgentBadge label={t('badge.urgent')} />}
          {closed && <span data-testid={`job-closed-${job.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border font-mono tracking-wide uppercase text-slate-300 bg-slate-500/15 border-slate-500/50"><Lock size={11} /> Completa</span>}
          {job.demo && <DemoBadge />}
        </div>

        <h3 className={`text-lg font-semibold leading-snug mb-3 transition-colors ${urgent ? 'text-red-200 group-hover:text-red-300' : 'text-white group-hover:text-amber-500'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
          {job.title}
        </h3>

        <div className="flex flex-wrap items-center gap-2 mb-4 text-sm text-slate-400">
          <span className="font-medium text-slate-300">{job.author.name}</span>
          {job.author.type === 'company' && job.author.verified && <VerifiedBadge label={t('badge.verified')} />}
          {job.author.type === 'professional' && <ProfessionalPublishedBadge label={t('badge.professionalPublished')} />}
        </div>

        <div className="space-y-2 text-sm text-slate-400 mb-4">
          <div className="flex items-center gap-2">
            <MapPin size={14} className="text-slate-500 shrink-0" />
            <span>{job.location}</span>
          </div>
          {!compact && (
            <div className="flex items-center gap-2">
              <Clock size={14} className="text-slate-500 shrink-0" />
              <span className="line-clamp-1">{job.schedule}</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Calendar size={14} className="text-slate-500 shrink-0" />
            <span className="font-mono text-xs uppercase tracking-wide">
              {t('label.deadline')}: {formatDate(job.deadline, locale)}
            </span>
          </div>
          {job.totalHours != null && (
            <div className="flex items-center gap-2" data-testid={`job-hours-${job.id}`}>
              <Clock size={14} className="text-slate-500 shrink-0" />
              <span className="font-mono text-xs uppercase tracking-wide text-amber-500/90">
                {job.totalHours} {t('label.hoursShort')} · {t('label.totalHours').toLowerCase()}
              </span>
            </div>
          )}
          {job.perDiem && job.perDiem !== 'none' && (
            <div className="flex items-center gap-2" data-testid={`job-perdiem-${job.id}`}>
              <UtensilsCrossed size={14} className="text-emerald-500 shrink-0" />
              <span className="font-mono text-xs uppercase tracking-wide text-emerald-400">{PER_DIEM_LABELS[job.perDiem]}</span>
            </div>
          )}
        </div>

        {!compact && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {job.skills.slice(0, 4).map((s) => (
              <span key={s} className="text-[11px] font-mono px-2 py-0.5 rounded border border-[#2A2F3D] text-slate-400">
                {s}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="pt-4 border-t border-[#2A2F3D] flex items-center justify-between">
        <div>
          <div className="text-amber-500 font-mono text-sm font-semibold">{job.compensationRaw}</div>
          <div data-testid={`job-card-applicants-${job.id}`} className="text-[11px] text-slate-500 font-mono uppercase tracking-wider mt-0.5">
            <Users size={11} className="inline mr-1" />{job.applicants} {t('label.applicants')}
          </div>
        </div>
        <span className="text-xs font-mono uppercase tracking-wider text-amber-500 group-hover:translate-x-1 transition-transform">
          {t('label.viewOffer')} →
        </span>
      </div>
    </Link>
  );
};

export const ProfessionalCard = ({ pro }) => {
  const { t } = useI18n();
  const cat = categoryById(pro.category);
  return (
    <article
      data-testid={`pro-card-${pro.id}`}
      className="sc-card p-6 flex flex-col h-full group"
    >
      <div className="flex items-start gap-4 mb-4">
        <div className="w-14 h-14 rounded-full bg-[#1A1D24] border border-[#2A2F3D] flex items-center justify-center text-amber-500 font-bold text-lg shrink-0">
          {pro.name.split(' ').map((n) => n[0]).slice(0, 2).join('')}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold text-white group-hover:text-amber-500 transition-colors truncate flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}><UserName userId={pro.id} testId={`pro-name-${pro.id}`} className="truncate">{pro.name}</UserName><OnlineDot userId={pro.id} /></h3>
          <p className="text-sm text-slate-400 truncate">{pro.role}</p>
          <div className="text-[11px] text-slate-500 font-mono uppercase tracking-wider mt-1">{pro.location} · {pro.experience}</div>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 mb-4">
        {cat && <CategoryTag name={cat.name} accent={cat.accent} />}
        {pro.demo && <DemoBadge />}
      </div>
      <div className="flex flex-wrap gap-1.5 mb-4">
        {pro.skills.slice(0, 4).map((s) => (
          <span key={s} className="text-[11px] font-mono px-2 py-0.5 rounded border border-[#2A2F3D] text-slate-400">{s}</span>
        ))}
      </div>
      <div className="mt-auto pt-4 border-t border-[#2A2F3D] flex items-center justify-between text-xs">
        {pro.rating != null ? <span className="font-mono text-amber-500">★ {Number(pro.rating).toFixed(1)}</span> : <span className="font-mono text-slate-500">{pro.role || 'Profesional'}</span>}
        <Link to={`/profesionales/${pro.id}`} data-testid={`pro-profile-${pro.id}`} className="font-mono uppercase tracking-wider text-slate-500 hover:text-amber-500 transition-colors">{t('label.viewProfile')} →</Link>
      </div>
    </article>
  );
};

export const CompanyCard = ({ company }) => {
  const { t } = useI18n();
  return (
    <article data-testid={`company-card-${company.id}`} className="sc-card p-6 flex flex-col h-full group">
      <div className="flex items-start gap-4 mb-4">
        <div className="w-12 h-12 rounded-lg bg-[#1A1D24] border border-[#2A2F3D] flex items-center justify-center text-amber-500 font-bold text-base shrink-0" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {company.name.split(' ').map((n) => n[0]).slice(0, 2).join('')}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base font-semibold text-white group-hover:text-amber-500 transition-colors truncate" style={{ fontFamily: 'Outfit, sans-serif' }}><UserName userId={company.id} testId={`company-name-${company.id}`} className="max-w-full truncate">{company.name}</UserName></h3>
            <OnlineDot userId={company.id} />
            {company.verified && <VerifiedBadge label={t('badge.verified')} />}
          </div>
          <div className="text-[11px] text-slate-500 font-mono uppercase tracking-wider mt-1">{company.location}</div>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 mb-4">
        {company.specialties.map((s) => (
          <span key={s} className="text-[11px] font-mono px-2 py-0.5 rounded border border-[#2A2F3D] text-slate-400">{s}</span>
        ))}
        {company.demo && <DemoBadge />}
      </div>
      <div className="mt-auto pt-4 border-t border-[#2A2F3D] flex items-center justify-between text-xs">
        {company.verified ? <span className="font-mono text-emerald-400">✓ {t('badge.verified')}</span> : <span className="font-mono text-slate-500">{company.sector || 'Empresa'}</span>}
        <Link to={`/empresas/${company.id}`} data-testid={`company-profile-${company.id}`} className="font-mono uppercase tracking-wider text-slate-500 hover:text-amber-500 transition-colors">{t('label.viewCompany')} →</Link>
      </div>
    </article>
  );
};

export const NewsCard = ({ item }) => {
  const { t, locale } = useI18n();
  return (
    <Link to={`/actualidad/${item.id}`} data-testid={`news-card-${item.id}`} className="sc-card overflow-hidden flex flex-col h-full group">
      <div className="aspect-[16/10] overflow-hidden bg-[#1A1D24] relative">
        <img src={item.image} alt={item.title} loading="lazy" className="w-full h-full object-cover opacity-85 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" />
        <div className="absolute top-3 left-3">
          <CategoryTag name={item.category} accent="#F59E0B" />
        </div>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="sc-mono-eyebrow mb-2 text-slate-500">{formatDate(item.date, locale)} · {item.source}</div>
        <h3 className="text-base font-semibold text-white group-hover:text-amber-500 transition-colors leading-snug mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.title}</h3>
        <p className="text-sm text-slate-400 leading-relaxed line-clamp-2 mb-3">{item.summary}</p>
        <div className="mt-auto pt-3 border-t border-[#2A2F3D] flex items-center justify-between">
          <span className="text-xs font-mono uppercase tracking-wider text-amber-500 group-hover:translate-x-1 transition-transform">{t('label.readMore')} →</span>
          {item.sourceUrl && (
            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 border border-[#2A2F3D] px-2 py-0.5 rounded">Fuente ↗</span>
          )}
        </div>
      </div>
    </Link>
  );
};

export const TrainingCard = ({ item }) => {
  const { t, locale } = useI18n();
  return (
    <Link to={`/formacion/${item.id}`} data-testid={`training-card-${item.id}`} className="sc-card p-6 flex flex-col h-full group">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <CategoryTag name={item.category} accent="#F59E0B" />
        {item.sponsored && <span className="text-[10px] font-mono uppercase tracking-wider text-amber-500 border border-amber-500/30 px-2 py-0.5 rounded bg-amber-500/5">{t('label.sponsored')}</span>}
        {item.demo && <DemoBadge />}
      </div>
      <h3 className="text-base font-semibold text-white group-hover:text-amber-500 transition-colors leading-snug mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.title}</h3>
      <div className="space-y-1.5 text-sm text-slate-400 mb-4">
        <div className="flex justify-between"><span className="text-slate-500 font-mono text-xs uppercase tracking-wider">{t('label.school')}</span><span>{item.school}</span></div>
        <div className="flex justify-between"><span className="text-slate-500 font-mono text-xs uppercase tracking-wider">{t('label.modality')}</span><span>{item.modality}</span></div>
        <div className="flex justify-between"><span className="text-slate-500 font-mono text-xs uppercase tracking-wider">{t('label.date')}</span><span className="font-mono text-xs">{formatDate(item.date, locale)}</span></div>
      </div>
      <div className="mt-auto pt-4 border-t border-[#2A2F3D] flex items-center justify-between">
        <span className="font-mono text-amber-500 font-semibold">{item.price}</span>
        <span className="text-xs font-mono uppercase tracking-wider text-slate-500 group-hover:text-amber-500 transition-colors">
          {item.enrollUrl ? 'Inscribirme →' : `${t('label.viewDetails')} →`}
        </span>
      </div>
    </Link>
  );
};

export const EventCard = ({ item }) => {
  const { t, locale } = useI18n();
  return (
    <Link to={`/eventos/${item.id}`} data-testid={`event-card-${item.id}`} className="sc-card p-6 flex flex-col h-full group">
      <div className="flex items-center gap-2 mb-3">
        <CategoryTag name={item.category} accent="#F59E0B" />
        {item.demo && <DemoBadge />}
      </div>
      <h3 className="text-lg font-semibold text-white group-hover:text-amber-500 transition-colors mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.title}</h3>
      <p className="text-sm text-slate-400 leading-relaxed mb-4 line-clamp-2">{item.summary}</p>
      <div className="mt-auto pt-4 border-t border-[#2A2F3D] flex items-center justify-between text-xs">
        <span className="font-mono text-amber-500">{formatDate(item.date, locale)}</span>
        <span className="text-slate-400">{item.location}</span>
      </div>
    </Link>
  );
};
